import React, { Component, createContext } from 'react';
import { Hub } from 'aws-amplify/utils';
import {
  getCurrentSession,
  getCurrentCredentials,
  signOut,
  getCurrentAuthenticatedUser,
} from '../apiServices/authApi';
import { getAllUsers, getUsersGroups, getCurrentUserInfo } from '../apiServices/userApi';
import { imageExists } from '../util/UtilFunctions';
import {
  getCurrentUserGroup,
  handleLoginRedirect,
  getIsWorkspaceExpired,
} from '../app/AppUtilityFunctions';
import store from '../configureStore';
import { updateHeader } from '../app/features/project/projectSlice';
import {
  setAuthUser,
  updateCurrentCredentials,
  setStatus,
  setOldUserDetails,
} from '../app/features/user/userSlice';
import { updateWorkspaceUsers } from '../app/features/workspace/workspaceSlice';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DynData = any;

export interface SessionContextValue {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isQuerying: boolean;
  init: DynData;
  groupsList: DynData[] | undefined;
  workspacePeopleList: DynData[] | undefined;
  rootWorkspaceDetails: DynData;
  currentItem: DynData;
  initiateSession: () => Promise<void>;
  updateCurrentSession: () => Promise<DynData>;
  updateGroupsList: (repeatId?: DynData, counter?: number) => Promise<void>;
  updateCurrentUserInfo: (shouldRepeat?: boolean, counter?: number) => Promise<void>;
  getWorkspacePeopleList: () => Promise<void>;
  userHasAuthenticated: (authenticated: boolean) => void;
  updateIsQuerying: (querying: boolean) => void;
  updateLoginProcessOngoing: (value: boolean) => void;
  updateRootWorkspaceDetails: (details: DynData) => void;
  updateCurrentItem: (item: DynData) => void;
  handleLogout: (noRedirect?: boolean) => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

// Shared ref so the class component can navigate without hooks
let navigateRef: { push: (path: string) => void; replace: (path: string) => void; goBack: () => void } | null = null;

export const setSessionNavigate = (navigate: (to: string | number, opts?: { replace?: boolean }) => void) => {
  navigateRef = {
    push: (path: string) => navigate(path),
    replace: (path: string) => navigate(path, { replace: true }),
    goBack: () => navigate(-1 as unknown as string),
  };
};

interface SessionState {
  getCurrentSessionProcess: { status: string; data: DynData; error: DynData };
  getCurrentCredentialsProcess: { status: string; data: DynData; error: DynData };
  getGroupsListProcess: { status: string; data: DynData; error: DynData };
  getAllUsersProcess: { status: string; data: DynData; error: DynData };
  init: DynData;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isQuerying: boolean;
  rootWorkspaceDetails: DynData;
  loginProcessOngoing: boolean;
  currentItem: DynData;
}

const initialState: SessionState = {
  getCurrentSessionProcess: { status: 'initial', data: undefined, error: undefined },
  getCurrentCredentialsProcess: { status: 'initial', data: undefined, error: undefined },
  getGroupsListProcess: { status: 'initial', data: undefined, error: undefined },
  getAllUsersProcess: { status: 'initial', data: undefined, error: undefined },
  init: undefined,
  isAuthenticated: false,
  isAuthenticating: true,
  isQuerying: false,
  rootWorkspaceDetails: undefined,
  loginProcessOngoing: false,
  currentItem: undefined,
};

interface SessionProviderProps {
  children: React.ReactNode;
  onWorkspaceExpired?: () => void;
}

class SessionProviderClass extends Component<SessionProviderProps, SessionState> {
  hubUnsubscribe: (() => void) | undefined;
  currentSessionInterval: ReturnType<typeof setInterval> | undefined;
  cancelled = false;

  constructor(props: SessionProviderProps) {
    super(props);

    this.hubUnsubscribe = Hub.listen('auth', async (data) => {
      const { payload } = data;
      if (payload.event === 'signedIn') {
        const cognitoUser = await getCurrentAuthenticatedUser();
        store.dispatch(setAuthUser(cognitoUser));
      }
    });

    this.state = initialState;
  }

  componentDidMount = async () => {
    this.addEventListeners();
    await this.initiateSession();
    this.currentSessionInterval = setInterval(() => this.updateCurrentSession(), 300000);

    const user = store.getState().user;
    if (!user.uid) {
      try {
        const cognitoUser = await getCurrentAuthenticatedUser();
        store.dispatch(setAuthUser(cognitoUser));
      } catch { /* Not authenticated */ }
    }
  };

  componentWillUnmount = () => {
    this.cancelled = true;
    clearInterval(this.currentSessionInterval);
    this.hubUnsubscribe?.();
    window.removeEventListener('focus', this.handleFocus);
    window.removeEventListener('dragover', this.preventUnwantedDragNDropEffect);
    window.removeEventListener('drop', this.preventUnwantedDragNDropEffect);
  };

  initiateSession = async () => {
    const { pathname } = window.location;
    if (pathname.includes('signout')) { await this.handleLogout(); return; }

    this.setState({ ...initialState });
    this.updateIsQuerying(true);
    await this.updateCurrentSession();
    await this.updateCurrentCredentials();

    if (!this.state.isAuthenticated) {
      this.setState({ isAuthenticating: false });
      this.updateIsQuerying(false);
      store.dispatch(setStatus('ready'));
      return;
    }

    this.updateGroupsList().then(() => {
      if (this.state.getGroupsListProcess.data?.length > 0) this.updateAllUsers();
      this.updateCurrentUserInfo(false, 0).then(() => {
        const workspace = store.getState().workspace;
        const user = store.getState().user;
        const expired = getIsWorkspaceExpired(this.state.getGroupsListProcess, user.oldUserDetails, workspace.currentUserGroup!);
        if (expired && this.props.onWorkspaceExpired) this.props.onWorkspaceExpired();
        if (navigateRef) {
          handleLoginRedirect(this.state.getGroupsListProcess, workspace.currentUserGroup, this.state.loginProcessOngoing, navigateRef);
        }
        this.setState({ isAuthenticating: false, loginProcessOngoing: false });
        this.updateIsQuerying(false);
      });
    });
    store.dispatch(setStatus('ready'));
  };

  updateCurrentSession = () =>
    new Promise<DynData>((resolve, reject) => {
      getCurrentSession().then((response) => {
        store.dispatch(updateHeader(response.idToken.jwtToken));
        this.setState({
          getCurrentSessionProcess: { status: 'success', data: response, error: undefined },
          init: { headers: { Authorization: response.idToken.jwtToken } },
        }, () => resolve(response));
      }).catch((error) => {
        this.updateIsQuerying(false);
        this.setState({ getCurrentSessionProcess: { status: 'error', data: undefined, error }, isAuthenticating: false });
        clearInterval(this.currentSessionInterval);
        reject(error);
      });
    });

  updateCurrentCredentials = () =>
    new Promise<DynData>((resolve, reject) => {
      getCurrentCredentials().then((response) => {
        const hasTokens = !!this.state.getCurrentSessionProcess.data?.idToken?.jwtToken;
        this.userHasAuthenticated(hasTokens);
        store.dispatch(updateCurrentCredentials(response.identityId));
        this.setState({ getCurrentCredentialsProcess: { status: 'success', data: response, error: undefined } }, () => resolve(response));
      }).catch((error) => {
        this.setState({ getCurrentCredentialsProcess: { status: 'error', data: undefined, error } });
        store.dispatch(updateCurrentCredentials(null));
        this.updateIsQuerying(false);
        reject(error);
      });
    });

  updateGroupsList = async (repeatId?: DynData, counter?: number) => {
    try {
      const response = await getUsersGroups();
      this.handleCurrentUserGroup(response);
      const items = response.response.Items;
      this.setState({ getGroupsListProcess: { status: 'success', data: items.length ? items : [], error: undefined } });
      if (repeatId) await this.handleGroupImageCheck(repeatId, counter ?? 0, response);
    } catch (error) {
      this.setState({ isAuthenticating: false, getGroupsListProcess: { status: 'error', data: undefined, error } });
      this.updateIsQuerying(false);
    }
  };

  handleCurrentUserGroup = (response: DynData) => { getCurrentUserGroup(response.response.Items); };

  handleGroupImageCheck = async (repeatId: DynData, counter: number, response: DynData) => {
    const gid = repeatId.S || repeatId;
    const group = response.response.Items.find((g: DynData) => g.id.S === gid);
    if (group?.image) {
      try { await imageExists(group.image.S); this.setState({ getGroupsListProcess: { status: 'success', data: response.response.Items, error: undefined } }); }
      catch { if (counter < 5) setTimeout(() => this.updateGroupsList(repeatId, counter + 1), 3000); }
    } else if (counter < 5) { setTimeout(() => this.updateGroupsList(repeatId, counter + 1), 3000); }
  };

  updateAllUsers = () =>
    getAllUsers().then(({ response }) => {
      this.setState({ getAllUsersProcess: { status: 'success', data: response.Items, error: undefined } });
      return response.Items;
    }).catch((err) => {
      this.setState({ getAllUsersProcess: { status: 'error', data: undefined, error: err }, isAuthenticating: false });
      this.updateIsQuerying(false);
    });

  getWorkspacePeopleList = async () => {
    try {
      this.updateIsQuerying(true);
      const response = await getAllUsers();
      this.updateIsQuerying(false);
      this.setState({ getAllUsersProcess: { status: 'success', data: response.response.Items, error: undefined } });
      store.dispatch(updateWorkspaceUsers(response.response));
    } catch { this.updateIsQuerying(false); }
  };

  updateCurrentUserInfo = async (shouldRepeat?: boolean, counter?: number) => {
    try {
      const currentUserGroup = store.getState().workspace.currentUserGroup;
      const response = await getCurrentUserInfo(currentUserGroup);
      if (shouldRepeat) this.handleUserImageCheck(response, shouldRepeat, counter ?? 0);
      else store.dispatch(setOldUserDetails(response.response.Item));
    } catch {
      this.setState({ isAuthenticating: false });
      this.updateIsQuerying(false);
    }
  };

  handleUserImageCheck = async (response: DynData, shouldRepeat: boolean, counter: number) => {
    if (response.response.Item.image) {
      try { await imageExists(response.response.Item.image.S); store.dispatch(setOldUserDetails(response.response.Item)); }
      catch { if (counter < 5) setTimeout(() => this.updateCurrentUserInfo(shouldRepeat, counter + 1), 1000); else store.dispatch(setOldUserDetails(response.response.Item)); }
    } else if (counter < 5) { setTimeout(() => this.updateCurrentUserInfo(shouldRepeat, counter + 1), 1000); }
    else { store.dispatch(setOldUserDetails(response.response.Item)); }
  };

  userHasAuthenticated = (authenticated: boolean) => { this.setState({ isAuthenticated: authenticated }); };

  handleLogout = async (noRedirect?: boolean) => {
    try {
      this.updateIsQuerying(true);
      await signOut();
      if (!noRedirect && navigateRef) navigateRef.push('/');
      this.updateIsQuerying(false);
      this.resetState();
      store.dispatch(setAuthUser(null));
      store.dispatch(setStatus('initial'));
    } catch { this.updateIsQuerying(false); }
  };

  updateIsQuerying = (querying: boolean) => {
    if (querying) {
      if (this.state.isQuerying) this.setState({ isQuerying: false }, () => this.setState({ isQuerying: true }));
      else this.setState({ isQuerying: true });
    } else { this.setState({ isQuerying: false }); }
  };

  updateLoginProcessOngoing = (value: boolean) => { this.setState({ loginProcessOngoing: value }); };
  updateRootWorkspaceDetails = (details: DynData) => { this.setState({ rootWorkspaceDetails: details }); };
  updateCurrentItem = (item: DynData) => { this.setState({ currentItem: item }); };
  resetState = () => { this.setState({ ...initialState, isAuthenticating: false }); };

  addEventListeners = () => {
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('dragover', this.preventUnwantedDragNDropEffect, false);
    window.addEventListener('drop', this.preventUnwantedDragNDropEffect, false);
  };

  preventUnwantedDragNDropEffect = (event: Event) => {
    if ((event.target as HTMLElement).id !== 'dropzone') event.preventDefault();
  };

  handleFocus = () => { this.updateCurrentSession(); };

  render() {
    const value: SessionContextValue = {
      isAuthenticated: this.state.isAuthenticated,
      isAuthenticating: this.state.isAuthenticating,
      isQuerying: this.state.isQuerying,
      init: this.state.init,
      groupsList: this.state.getGroupsListProcess.data,
      workspacePeopleList: this.state.getAllUsersProcess.data,
      rootWorkspaceDetails: this.state.rootWorkspaceDetails,
      currentItem: this.state.currentItem,
      initiateSession: this.initiateSession,
      updateCurrentSession: this.updateCurrentSession,
      updateGroupsList: this.updateGroupsList,
      updateCurrentUserInfo: this.updateCurrentUserInfo,
      getWorkspacePeopleList: this.getWorkspacePeopleList,
      userHasAuthenticated: this.userHasAuthenticated,
      updateIsQuerying: this.updateIsQuerying,
      updateLoginProcessOngoing: this.updateLoginProcessOngoing,
      updateRootWorkspaceDetails: this.updateRootWorkspaceDetails,
      updateCurrentItem: this.updateCurrentItem,
      handleLogout: this.handleLogout,
    };
    return (
      <SessionContext.Provider value={value}>
        {this.props.children}
      </SessionContext.Provider>
    );
  }
}

export { SessionContext, SessionProviderClass as SessionProvider };
export default SessionContext;
